import React, { useEffect, useRef, useState } from 'react';
import './ProgramOutcomesTable.css';
import { sanitizePublicText } from '../utils/publicText';
import { downloadProgramOutcomesPdf } from '../utils/programOutcomesPdf';

const DURUM_LABELS = {
  'aynı': 'Değişmedi',
  'güncellendi': 'Güncellendi',
  'yeni': 'Yeni eklenen',
  'birleştirildi': 'Birleştirildi',
  'kaldirildi': 'Kaldırılan',
};

const DURUM_CLASS = {
  'aynı': 'po-diff-row--same',
  'güncellendi': 'po-diff-row--updated',
  'yeni': 'po-diff-row--added',
  'birleştirildi': 'po-diff-row--merged',
  'kaldirildi': 'po-diff-row--removed',
};

const VISIBLE_SUGGESTION_STATUSES = new Set(['aynı', 'güncellendi', 'yeni']);

const normalizeOutcomeNumber = (value) => {
  const text = String(value ?? '').trim().toUpperCase();
  const numericPart = text.match(/\d+/);
  return numericPart ? numericPart[0] : text.replace(/[^A-ZÇĞİÖŞÜ0-9]/g, '');
};

const normalizeOutcomeText = (value) => String(value ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('tr-TR');

function ProgramOutcomesTable({
  eskiCiktilar, yeniCiktilar, kaldirilanlar,
  programName, planInstructions, onPlanInstructionsChange,
  onRegenerate, onContinueToPlan, onCancel,
}) {
  const nextKey = useRef(0);
  const createFinalRow = (outcome, source) => ({
    ...outcome,
    name: String(outcome?.name || '').trim(),
    _key: `final-${nextKey.current++}`,
    _source: source,
  });

  const [saveError, setSaveError] = useState('');
  const [finalRows, setFinalRows] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [manualText, setManualText] = useState('');
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState('');

  useEffect(() => {
    setFinalRows([]);
    setEditingKey(null);
    setManualText('');
    setSaveError('');
    setPdfError('');
    // createFinalRow intentionally produces fresh local editor keys for each proposal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yeniCiktilar]);

  const selectedTexts = new Set(finalRows.map((row) => normalizeOutcomeText(row.name)));
  const isSelected = (outcome) => selectedTexts.has(normalizeOutcomeText(outcome?.name));

  const addToFinal = (outcome, source) => {
    const name = String(outcome?.name || '').trim();
    if (!name || selectedTexts.has(normalizeOutcomeText(name))) return;
    if (finalRows.length >= 13) {
      setSaveError('Nihai program çıktısı sayısı en fazla 13 olabilir.');
      return;
    }
    setFinalRows((previous) => [...previous, createFinalRow({ ...outcome, name }, source)]);
    setSaveError('');
  };

  const addManualOutcome = () => {
    const name = manualText.trim();
    if (!name) {
      setSaveError('Yeni program çıktısı metnini giriniz.');
      return;
    }
    if (selectedTexts.has(normalizeOutcomeText(name))) {
      setSaveError('Bu program çıktısı nihai listede zaten bulunuyor.');
      return;
    }
    if (finalRows.length >= 13) {
      setSaveError('Nihai program çıktısı sayısı en fazla 13 olabilir.');
      return;
    }
    setFinalRows((previous) => [...previous, createFinalRow({ name, durum: 'yeni' }, 'manual')]);
    setManualText('');
    setSaveError('');
  };

  const removeFinalRow = (key) => {
    setFinalRows((previous) => previous.filter((row) => row._key !== key));
    if (editingKey === key) setEditingKey(null);
    setSaveError('');
  };

  const updateFinalRow = (key, value) => {
    setFinalRows((previous) => previous.map((row) => (
      row._key === key ? { ...row, name: value } : row
    )));
  };

  const finishEditing = (key) => {
    const row = finalRows.find((item) => item._key === key);
    if (!row?.name.trim()) {
      setSaveError('Program çıktısı boş bırakılamaz. Metni tamamlayın veya çıktıyı silin.');
      return;
    }
    updateFinalRow(key, row.name.trim());
    setEditingKey(null);
    setSaveError('');
  };

  const moveFinalRow = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= finalRows.length) return;
    setFinalRows((previous) => {
      const reordered = [...previous];
      [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
      return reordered;
    });
  };

  const handleDownloadPdf = async () => {
    if (finalRows.length === 0 || downloadingPdf) return;

    const outcomes = finalRows.map((row) => ({ name: String(row.name || '').trim() }));
    if (outcomes.some((outcome) => !outcome.name)) {
      setPdfError('PDF oluşturmadan önce boş program çıktısını tamamlayın veya listeden çıkarın.');
      return;
    }

    setDownloadingPdf(true);
    setPdfError('');
    try {
      await downloadProgramOutcomesPdf(outcomes, { programName });
    } catch (error) {
      console.error('Nihai program çıktıları PDF hatası:', error);
      setPdfError('PDF oluşturulamadı. Lütfen tekrar deneyin.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const dispositionByNumber = new Map(
    (kaldirilanlar || [])
      .map((item) => [normalizeOutcomeNumber(item.number), item])
      .filter(([number]) => Boolean(number)),
  );

  const existingRows = (eskiCiktilar || []).map((outcome, index) => {
    const disposition = dispositionByNumber.get(normalizeOutcomeNumber(outcome.number));
    const merged = disposition?.islem === 'birleştirildi';
    const removed = disposition?.islem === 'kaldirildi';
    return { outcome, index, disposition, merged, removed };
  });

  const handleContinueToPlan = () => {
    const cleanedRows = finalRows.map((row) => ({ ...row, name: row.name.trim() }));
    if (cleanedRows.length === 0) {
      setSaveError('Nihai listede en az bir program çıktısı olmalı.');
      return;
    }
    if (cleanedRows.some((row) => !row.name)) {
      setSaveError('Program çıktıları boş bırakılamaz.');
      return;
    }
    if (cleanedRows.length > 13) {
      setSaveError('Nihai program çıktısı sayısı en fazla 13 olabilir.');
      return;
    }
    const uniqueNames = new Set(cleanedRows.map((row) => normalizeOutcomeText(row.name)));
    if (uniqueNames.size !== cleanedRows.length) {
      setSaveError('Nihai listede aynı program çıktısı birden fazla kez bulunamaz.');
      return;
    }

    setSaveError('');
    const result = onContinueToPlan(
      cleanedRows.map((outcome, index) => ({
        number: index + 1,
        name: outcome.name,
      })),
      String(planInstructions || '').trim(),
    );
    if (result?.error) setSaveError(result.error);
  };

  const renderSourceAction = (outcome, source) => {
    const selected = isSelected(outcome);
    const limitReached = finalRows.length >= 13;
    return (
      <button
        type="button"
        className={`po-source-select${selected ? ' po-source-select--selected' : ''}`}
        onClick={() => addToFinal(outcome, source)}
        disabled={selected || limitReached}
        aria-label={selected ? 'Nihai listede' : (limitReached ? 'En fazla 13 PÇ eklenebilir' : 'Nihai listeye ekle')}
      >
        Seç
      </button>
    );
  };

  return (
    <div className="po-diff">
      <div className="po-diff__intro">
        Mevcut veya önerilen çıktılardan seçim yapın. Seçimleriniz alttaki nihai listede düzenlenebilir.
      </div>

      <div className="po-diff__legend">
        <span><span className="po-legend-dot po-legend-dot--added" />Yeni eklenen</span>
        <span><span className="po-legend-dot po-legend-dot--updated" />Güncellendi</span>
        <span><span className="po-legend-dot po-legend-dot--same" />Değişmedi</span>
        <span><span className="po-legend-dot po-legend-dot--merged" />Birleştirildi</span>
        <span><span className="po-legend-dot po-legend-dot--removed" />Kaldırılan</span>
      </div>

      <div className="po-diff__grid">
        <section className="po-diff__side po-diff__side--eski">
          <div className="po-diff__side-label">Mevcut Program Çıktıları</div>
          {existingRows.length === 0 ? (
            <div className="po-diff__empty">Mevcut çıktı yok.</div>
          ) : existingRows.map(({ outcome, index, disposition, merged, removed }) => (
            <div
              key={`existing-${index}`}
              className={`po-diff-row ${merged ? DURUM_CLASS.birleştirildi : (removed ? DURUM_CLASS.kaldirildi : '')}`}
            >
              <div className="po-diff-row__head">
                <strong>{outcome.number || index + 1}.</strong>
                {merged && <span className="po-diff-badge po-diff-badge--merged">Birleştirildi</span>}
                {removed && <span className="po-diff-badge po-diff-badge--removed">Kaldırılan</span>}
              </div>
              <div className="po-source-text">{outcome.name}</div>
              {disposition?.aciklama && (
                <div className="po-diff-row__disposition">{sanitizePublicText(disposition.aciklama)}</div>
              )}
              {renderSourceAction(outcome, 'existing')}
            </div>
          ))}
        </section>

        <section className="po-diff__side po-diff__side--yeni">
          <div className="po-diff__side-label">Önerilen Yeni Liste</div>
          {(yeniCiktilar || []).filter((outcome) => VISIBLE_SUGGESTION_STATUSES.has(outcome.durum)).length === 0 ? (
            <div className="po-diff__empty">Önerilen çıktı yok.</div>
          ) : (yeniCiktilar || [])
            .filter((outcome) => VISIBLE_SUGGESTION_STATUSES.has(outcome.durum))
            .map((outcome, index) => (
            <div key={`suggested-${index}`} className={`po-diff-row ${DURUM_CLASS[outcome.durum] || ''}`}>
              <div className="po-diff-row__head">
                <strong>{index + 1}.</strong>
                {outcome.durum && outcome.durum !== 'aynı' && (
                  <span className={`po-diff-badge po-diff-badge--${outcome.durum}`}>
                    {DURUM_LABELS[outcome.durum] || outcome.durum}
                  </span>
                )}
              </div>
              <div className="po-source-text">{outcome.name}</div>
              {outcome.aciklama && (
                <div className="po-diff-row__aciklama">{sanitizePublicText(outcome.aciklama)}</div>
              )}
              {renderSourceAction(outcome, 'suggested')}
            </div>
            ))}
        </section>
      </div>

      <section className="po-final">
        <div className="po-final__header">
          <div>
            <h3>Nihai Program Çıktıları</h3>
            <p>Metinleri düzenleyebilir, sıralayabilir veya listeden çıkarabilirsiniz.</p>
          </div>
          <div className="po-final__header-actions">
            <span className="po-final__count">{finalRows.length} PÇ · En fazla 13</span>
            <button
              type="button"
              className="po-final__pdf-button"
              onClick={handleDownloadPdf}
              disabled={finalRows.length === 0 || downloadingPdf}
            >
              {downloadingPdf ? 'PDF Hazırlanıyor…' : 'PDF Olarak İndir'}
            </button>
          </div>
        </div>

        <div className="po-manual-add">
          <textarea
            value={manualText}
            onChange={(event) => setManualText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                addManualOutcome();
              }
            }}
            rows="2"
            placeholder="Yeni program çıktısını giriniz…"
            aria-label="Yeni program çıktısı"
          />
          <button type="button" onClick={addManualOutcome} disabled={finalRows.length >= 13}>+ Yeni Çıktı Ekle</button>
        </div>

        {finalRows.length === 0 ? (
          <div className="po-final__empty">Henüz seçim yapılmadı. Üstteki listelerden bir PÇ seçin veya yeni bir çıktı oluşturun.</div>
        ) : (
          <ol className="po-final__list">
            {finalRows.map((outcome, index) => (
              <li key={outcome._key} className="po-final-row">
                <span className="po-final-row__number">{index + 1}</span>
                <div className="po-final-row__content">
                  {editingKey === outcome._key ? (
                    <textarea
                      autoFocus
                      value={outcome.name}
                      onChange={(event) => updateFinalRow(outcome._key, event.target.value)}
                      onBlur={() => finishEditing(outcome._key)}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape') setEditingKey(null);
                        if (event.key === 'Enter' && !event.shiftKey) {
                          event.preventDefault();
                          finishEditing(outcome._key);
                        }
                      }}
                      rows={Math.max(2, Math.ceil((outcome.name || '').length / 85))}
                    />
                  ) : (
                    <button type="button" className="po-final-row__text" onClick={() => setEditingKey(outcome._key)}>
                      <span>{outcome.name}</span><span aria-hidden="true">✎</span>
                    </button>
                  )}
                  <span className={`po-final-row__source po-final-row__source--${outcome._source}`}>
                    {outcome._source === 'existing' ? 'Mevcut plandan' : outcome._source === 'manual' ? 'Kullanıcı tarafından eklendi' : 'Önerilen plandan'}
                  </span>
                </div>
                <div className="po-final-row__controls">
                  <button type="button" onClick={() => moveFinalRow(index, -1)} disabled={index === 0} title="Yukarı taşı">↑</button>
                  <button type="button" onClick={() => moveFinalRow(index, 1)} disabled={index === finalRows.length - 1} title="Aşağı taşı">↓</button>
                  <button type="button" className="po-final-row__delete" onClick={() => removeFinalRow(outcome._key)} title="Listeden çıkar">×</button>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="po-plan-instructions" aria-labelledby="po-plan-instructions-title">
        <div className="po-plan-instructions__header">
          <div>
            <h3 id="po-plan-instructions-title">Ders Planı İçin Ek Talimat</h3>
            <p>Plan oluşturulurken dikkate alınmasını istediğiniz ölçütleri yazabilirsiniz.</p>
          </div>
          <span>Opsiyonel</span>
        </div>
        <textarea
          value={planInstructions}
          onChange={(event) => onPlanInstructionsChange(event.target.value)}
          rows={3}
          maxLength={2000}
          placeholder="Örnek: İlk iki dönemde temel alan derslerine ağırlık ver; uygulamalı dersleri artır; mevcut X dersini koru; seçmeli dersleri 5. dönemden itibaren planla."
          aria-label="Ders planı için ek talimat"
        />
        <div className="po-plan-instructions__meta">
          <span>Bu talimat yalnızca ders planı önerisinde kullanılacaktır.</span>
          <span>{String(planInstructions || '').length}/2000</span>
        </div>
      </section>

      {pdfError && <div className="po-diff__error" role="alert">{pdfError}</div>}

      {saveError && <div className="po-diff__error" role="alert">{saveError}</div>}

      <div className="po-diff__actions">
        <button className="button po-diff__cancel-btn" onClick={onCancel}>Vazgeç</button>
        <button className="button po-diff__regenerate-btn" onClick={onRegenerate}>Yeniden Öner</button>
        <button className="button po-diff__plan-btn" onClick={handleContinueToPlan} disabled={finalRows.length === 0}>
          Bu PÇ'lere Göre Ders Planı Öner
        </button>
      </div>
    </div>
  );
}

export default ProgramOutcomesTable;
